import { motion } from "framer-motion";
import { ArrowRight, Compass, Lightbulb, Lock, MousePointerClick } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SupportButton } from "@/components/SupportDialog";
import { useAuth } from "@/context/AuthContext";
import { canSee, isAdminRole } from "@/lib/api";

const canOpen = (u, a) => (a.admin ? isAdminRole(u) : !a.area || canSee(u, a.area));

function Steps({ steps }) {
  return (
    <ol className="space-y-2" data-testid="help-answer-steps">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-2.5 text-sm">
          <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary/15 font-mono text-[11px] font-semibold text-primary">{i + 1}</span>
          <span className="leading-relaxed">{s}</span>
        </li>
      ))}
    </ol>
  );
}

function Answer({ a, onNavigate, onTour, onGuide }) {
  const { session } = useAuth();
  return (
    <div className="space-y-3">
      <p className="font-display text-[15px] font-semibold leading-snug" data-testid="help-answer-title">{a.title}</p>
      <p className="text-sm leading-relaxed text-muted-foreground">{a.text}</p>
      {a.steps.length > 0 && <Steps steps={a.steps} />}
      {a.tip && <p className="flex gap-2 rounded-lg bg-amber-500/10 px-3 py-2 text-xs leading-relaxed text-amber-700 dark:text-amber-300"><Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0" />{a.tip}</p>}
      {a.guia?.length > 0 && onGuide && canOpen(session.user, a) && (
        <Button size="sm" onClick={() => onGuide(a)} className="mr-2" data-testid="help-guide-btn"><MousePointerClick className="mr-1 h-4 w-4" />Mostrar onde clicar</Button>)}
      {a.action === "tour" && onTour ? <Button size="sm" onClick={onTour} data-testid="help-start-tour-btn"><Compass className="mr-1 h-4 w-4" />Iniciar tour</Button> : a.to && (canOpen(session.user, a)
        ? <Button size="sm" variant="outline" onClick={() => onNavigate(a.to)} data-testid="help-open-screen-btn">Abrir {a.label}<ArrowRight className="ml-1 h-4 w-4" /></Button>
        : <p className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="help-no-access"><Lock className="h-3.5 w-3.5" />Seu usuário não tem acesso a esta tela. Fale com o administrador.</p>)}
    </div>
  );
}

// Resposta do assistente inteligente: texto livre (parágrafos e passos numerados) + botões das telas citadas.
function AiAnswer({ m, onNavigate, onTour }) {
  const { session } = useAuth();
  const linhas = (m.text || "").split(/\n+/).map((l) => l.trim()).filter(Boolean);
  return (
    <div className="space-y-2.5" data-testid="help-ai-answer">
      {linhas.map((l, i) => {
        const passo = l.match(/^(\d+)[.)]\s+(.*)$/);
        return passo ? (
          <p key={i} className="flex gap-2.5 text-sm">
            <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary/15 font-mono text-[11px] font-semibold text-primary">{passo[1]}</span>
            <span className="leading-relaxed">{passo[2].replace(/\*\*/g, "")}</span>
          </p>
        ) : <p key={i} className="text-sm leading-relaxed">{l.replace(/^[-•]\s+/, "• ").replace(/\*\*/g, "")}</p>;
      })}
      {(m.links || []).map((a) => (a.action === "tour" && onTour
        ? <Button key={a.id} size="sm" onClick={onTour}><Compass className="mr-1 h-4 w-4" />Iniciar tour</Button>
        : a.to && canOpen(session.user, a) && (
          <Button key={a.id} size="sm" variant="outline" onClick={() => onNavigate(a.to)} className="mr-2" data-testid="help-open-screen-btn">
            Abrir {a.label}<ArrowRight className="ml-1 h-4 w-4" /></Button>)))}
    </div>
  );
}

function Chips({ items, label, onAsk }) {
  if (!items?.length) return null;
  return (
    <div className="space-y-1.5">
      <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((s) => (
          <button key={s.id} type="button" onClick={() => onAsk(s.title, s.id)} className="rounded-full border bg-background px-3 py-1.5 text-left text-xs transition-colors hover:border-primary/50 hover:text-primary" data-testid={`help-suggestion-${s.id}`}>{s.title}</button>
        ))}
      </div>
    </div>
  );
}

export function HelpMessage({ m, index, last, onAsk, onNavigate, onTour, onGuide }) {
  const user = m.role === "user";
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className={user ? "flex justify-end" : "space-y-2"} data-testid={`help-msg-${index}`} data-role={m.role}>
      {user ? <p className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-primary px-3.5 py-2.5 text-sm text-primary-foreground">{m.text}</p> : (
        <>
          <div className="max-w-[92%] rounded-2xl rounded-bl-md bg-muted/70 px-4 py-3" data-testid={`help-bot-${m.type}`}>
            {m.type === "answer" ? <Answer a={m.article} onNavigate={onNavigate} onTour={onTour} onGuide={onGuide} />
              : m.type === "ai" ? <AiAnswer m={m} onNavigate={onNavigate} onTour={onTour} />
                : <p className="text-sm leading-relaxed">{m.text}</p>}
          </div>
          {last && <Chips items={m.suggestions} label={m.type === "answer" || m.type === "ai" ? "Veja também" : "Talvez seja isto"} onAsk={onAsk} />}
          {last && (m.type === "fallback" || m.type === "aviso") && <div className="max-w-[260px]"><SupportButton /></div>}
        </>
      )}
    </motion.div>
  );
}
